import { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import * as userService from '../../services/userService';

interface ResetTwoFactorPopupProps {
  id: string;
  username?: string;
  onClose: () => void;
}

// The caller proves themselves with a current code from their own authenticator. The server answers a
// refused reset with 403/404/409/429 {code, message}; show its message, keep the dialog open and clear
// the code (a code is never retried as is). Never log the caller out here: a refusal is not an expired session.
const ResetTwoFactorPopup = ({ id, username, onClose }: ResetTwoFactorPopupProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const otpValid = /^[0-9]{6}$/.test(otp);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await userService.resetTwoFactor(id, otp);
      notify(t('module.user.resetTwoFactorSuccess'), { type: 'success' });
      onClose();
    } catch (error: any) {
      notify(error?.response?.data?.message ?? t('module.user.resetTwoFactorFailed'), {
        type: 'error',
      });
      setOtp('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {t('module.user.resetTwoFactor')}
        {username ? ` - ${username}` : ''}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t('module.user.resetTwoFactorConfirm', { username: username ?? id })}</DialogContentText>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label={t('module.user.yourTotpCode')}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, pattern: '[0-9]*' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('module.user.buttons.cancel')}</Button>
        <Button variant="contained" disabled={isLoading || !otpValid} onClick={handleConfirm}>
          {t('ra.action.confirm')}
        </Button>
      </DialogActions>
    </>
  );
};

export default ResetTwoFactorPopup;
